import { useState, useCallback, useContext, useMemo } from "react";

import {
  PersonalDonationChargeResponse,
  DonationChargeResponse,
  DonationBoostResponse,
  DonationResponse,
} from "@every.org/common/src/codecs/entities";
import { SafeInt } from "@every.org/common/src/codecs/number";
import { Username } from "@every.org/common/src/codecs/username";
import { TypeOfPromise } from "@every.org/common/src/helpers/types";
import {
  getDonationRouteSpec,
  getDonationByShortIdRouteSpec,
} from "@every.org/common/src/routes/donation";

import {
  NonprofitsContext,
  addNonprofits,
} from "src/context/NonprofitsContext";
import { UsersContext } from "src/context/UsersContext";
import { addUser } from "src/context/UsersContext/actions";
import {
  ViewDonationContext,
  ViewDonationData,
} from "src/context/ViewDonationContext";
import { useAsyncEffect } from "src/hooks/useAsyncEffect";
import { queryApi } from "src/utility/apiClient";

export enum GetDonationStatus {
  LOADING = "LOADING",
  FETCH_ERROR = "FETCH_ERROR",
  SUCCESS = "SUCCESS",
}

export type GetDonationArgs =
  | { donationId: DonationResponse["id"] }
  | { shortId: number; nonprofitSlug: string; username: Username }
  | null;

/**
 * If data is in location state (i.e. from a Read More link), gets data
 * regarding the donation from there; else fetches the API for data
 */
export function useDonationFromContext(
  args: GetDonationArgs,
  initialData?: ViewDonationData
): RetrieveDonationDataResult | null {
  const [donationCharge, setDonationCharge] = useState<
    PersonalDonationChargeResponse | DonationChargeResponse | undefined
  >(initialData?.donationCharge);
  const [boost, setBoost] = useState<DonationBoostResponse | undefined>(
    initialData?.boost
  );
  const nonprofitContext = useContext(NonprofitsContext);
  const usersContext = useContext(UsersContext);
  const { donationData = null } = useContext(ViewDonationContext) || {};
  const [fetchError, setFetchError] = useState<Error | null>(null);

  // Extract and memoize these to avoid unnecessary refetches in case client
  // failed to memoize properly
  const { donationId: argsDonationId = null } =
    args && "donationId" in args ? args : {};
  const {
    shortId = null,
    nonprofitSlug = null,
    username = null,
  } = args && "shortId" in args ? args : {};
  const memoizedArgs: GetDonationArgs = useMemo(
    () =>
      argsDonationId
        ? { donationId: argsDonationId }
        : shortId && nonprofitSlug && username
        ? { shortId, nonprofitSlug, username }
        : null,
    [argsDonationId, nonprofitSlug, shortId, username]
  );

  const fetchDonation = useCallback(() => {
    if (!memoizedArgs) {
      return Promise.resolve(undefined);
    }

    // Based on donationId
    const requestedId =
      "donationId" in memoizedArgs ? memoizedArgs.donationId : null;
    if (requestedId && donationData) {
      if (donationData.donationCharge.donation.id !== requestedId) {
        return Promise.resolve(undefined);
      }
      return Promise.resolve(donationData);
    }

    if ("donationId" in memoizedArgs) {
      return queryApi(getDonationRouteSpec, {
        routeTokens: { identifier: memoizedArgs.donationId },
        queryParams: {},
        body: {},
      });
    }

    // Based on slug/username/shortId
    const { shortId, nonprofitSlug, username } = memoizedArgs;
    if (donationData) {
      const {
        user: { username: fromUsername },
        nonprofit: { primarySlug: toNonprofitSlug },
        donationCharge: {
          donation: { shortId: donationDataShortId },
        },
      } = donationData;

      if (
        fromUsername == username &&
        toNonprofitSlug == nonprofitSlug &&
        donationDataShortId == (shortId || 0)
      ) {
        return Promise.resolve(donationData);
      }
    }

    return queryApi(getDonationByShortIdRouteSpec, {
      routeTokens: {
        nonprofitSlug,
        shortId: Math.floor(shortId).toFixed(0) as unknown as SafeInt,
        username,
      },
      queryParams: {},
      body: {},
    });
  }, [memoizedArgs, donationData]);
  type FetchDonationResponse = TypeOfPromise<ReturnType<typeof fetchDonation>>;

  const handleFetchDonation = useCallback((resp: FetchDonationResponse) => {
    if (!resp) {
      return;
    }

    setDonationCharge(resp.donationCharge);
    setBoost(resp.boost);
    addNonprofits([
      {
        ...resp.nonprofit,
        supporterCount: resp.nonprofitSupporterCount || undefined,
        nonprofitTags: resp.nonprofitTags,
      },
    ]);
    addUser(resp.user);
  }, []);
  useAsyncEffect({
    asyncOperation: fetchDonation,
    handleResponse: handleFetchDonation,
    handleError: useCallback((err) => {
      setFetchError(err);
    }, []),
  });

  const nonprofit = donationCharge?.toNonprofitId
    ? nonprofitContext.nonprofitsById.get(donationCharge?.toNonprofitId)
    : undefined;
  const user = donationCharge?.fromUserId
    ? usersContext.usersById.get(donationCharge?.fromUserId)
    : undefined;

  if (!memoizedArgs) {
    return null;
  }
  return fetchError
    ? { status: GetDonationStatus.FETCH_ERROR, error: fetchError }
    : donationCharge && nonprofit && user && boost && typeof user !== "symbol"
    ? {
        status: GetDonationStatus.SUCCESS,
        donationCharge,
        nonprofit,
        user,
        boost,
      }
    : { status: GetDonationStatus.LOADING };
}

type RetrieveDonationDataResult =
  | { status: GetDonationStatus.LOADING }
  | { status: GetDonationStatus.FETCH_ERROR; error: Error }
  | ({ status: GetDonationStatus.SUCCESS } & ViewDonationData);
